import React, {useEffect, useMemo, useState} from 'react';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {useLocation, useParams} from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  styled,
  Table,
  TableBody,
  TableContainer,
  Typography,
} from '@mui/material';
import PageTitle from '../shared/PageTitle';
import {getPlayersTeamSessionGroupAsync} from '../../store/teamSessionGroupSlice';
import BackButton from '../shared/BackButton';
import SubtitleContainer from '../shared/SubtitleContainer';
import PageSubtitle from '../shared/PageSubtitle';
import {DateTime} from 'luxon';
import SessionGridItem from '../shared/SessionGridItem';
import {getTime} from '../../services/dateService';
import {getIntensityText} from '../../services/playerSessionService';
import {
  getSpeedMultiplier,
  getComparisonFromBaseline,
  getEnergyRatioFromFloat,
  getEnergyRatio,
} from '../../services/measurementService';
import SessionListItem from './SessionListItem';
import {SizeMe} from 'react-sizeme';
import {VictoryAxis} from 'victory';
import {yAxisStyle} from '../../services/graphService';
import PlayerSessionGroupLineChart from './PlayerSessionGroupLineChart';
import {TeamSession} from '../../interfaces';
import PlayerSessionShifts from '../shared/PlayerSessionShifts';
import SessionVideoPlayer from '../shared/SessionVideoPlayer';
import {getPlayersTeamGroupSessionTotals} from '../../services/teamSessionGroupService';
import {Roles, SessionTypes} from '../../enums';
import {getPlayerSessionGroupTag} from '../../services/playerSessionGroupService';
import PlayerSessionHeader from './PlayerSessionHeader';
import ExportSessionModal from '../shared/ExportSessionModal';
import SessionMetaData from '../shared/SessionMetaData';

const ShiftTitleWrapper = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

interface Props {
  playerView?: boolean;
}

const PlayerSessionGroupDetail: React.FC<Props> = ({playerView}) => {
  const {
    teamSessionGroupId,
    profileId,
    teamId: teamIdParam,
    teamSessionId,
  } = useParams();
  const location = useLocation();
  const hash = location.hash;

  const [showShiftReview, setShowShiftReview] = useState<boolean>(false);
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const {teamSessionGroup, profile, family} = useAppSelector(state => ({
    teamSessionGroup: state.teamSessionGroup,
    profile: state.profile,
    family: state.family,
  }));

  let teamId = parseInt(teamIdParam ?? '');
  let backPath = '/';
  let backText = t('navigation.back', {pageName: t('pages.playerSessions')});
  if (profile?.profile?.role === Roles.admin && profileId && !teamIdParam) {
    // Admin arrived from the admin view of the player's sessions list.
    backPath = `/admin/profiles/${profileId}/playersessions`;
  } else if (profile?.profile?.role === Roles.parent) {
    backPath = `/family/children/${profileId}/playersessions`;
    backText = t('navigation.back', {pageName: t('pages.playerSessions')});
  } else if (teamId && profileId && hash === '#tsg') {
    backPath = `/teams/${teamId}/teamsessiongroups/${teamSessionGroupId}`;
    backText = t('navigation.back', {
      pageName: t('pages.teamSessionGroupDetail'),
    });
  } else if (teamId && profileId) {
    backPath = `/teams/${teamId}/players/${profileId}/playersessions`;
    backText = t('navigation.back', {pageName: t('pages.playerSessions')});
  } else if (teamId && teamSessionId) {
    backPath = `/teams/${teamId}/teamsessions/${teamSessionId}`;
    backText = t('navigation.back', {pageName: t('pages.teamSessionDetail')});
  }

  if (!teamId) {
    teamId = teamSessionGroup.teamSessionGroup?.teamSessions?.length
      ? teamSessionGroup.teamSessionGroup.teamSessions.find(ts => ts.teamId)
          ?.teamId || 0
      : 0;
  }

  const title = t('navigation.pageTitle', {
    pageName: t('pages.playerSessionGroupDetail'),
  });

  const StyledSessionType = styled('span')(() => ({
    display: 'block',
    textTransform: 'capitalize',
  }));

  const sessionType = useMemo(
    () =>
      teamSessionGroup.teamSessionGroup?.teamSessions?.length &&
      teamSessionGroup.teamSessionGroup?.teamSessions[0]?.type,
    [teamSessionGroup.teamSessionGroup?.teamSessions],
  );
  const finalTeamSession = useMemo(
    () =>
      teamSessionGroup.teamSessionGroup?.teamSessions?.length
        ? [...teamSessionGroup.teamSessionGroup.teamSessions].sort(
            (a: TeamSession, b: TeamSession) => {
              const aStartTime = a.startTime ? new Date(a.startTime) : null;
              const bStartTime = b.startTime ? new Date(b.startTime) : null;
              if (!aStartTime || !bStartTime) {
                return 0;
              }
              return bStartTime.getTime() - aStartTime.getTime();
            },
          )[0]
        : null,
    [teamSessionGroup.teamSessionGroup?.teamSessions],
  );
  useEffect(() => {
    // if profile is specified in the URL path
    if (teamSessionGroupId && profileId) {
      dispatch(
        getPlayersTeamSessionGroupAsync({
          id: parseInt(teamSessionGroupId),
          profileId: parseInt(profileId),
        }),
      );
    }
    // else if profile is specified in the profile state
    else if (teamSessionGroupId && profile?.profile?.id) {
      dispatch(
        getPlayersTeamSessionGroupAsync({
          id: parseInt(teamSessionGroupId),
          profileId: profile.profile.id,
        }),
      );
    }
  }, [teamSessionGroupId, profileId, dispatch, profile.profile.id]);

  const speedMultiplier = getSpeedMultiplier(
    profile?.profile?.preferredUnits === 'metric',
  );
  const isMetric = profile.profile?.preferredUnits === 'metric';
  const speedUnitLabel = isMetric
    ? (t('general.units.kphAbbrev') as string)
    : (t('general.units.mphAbbrev') as string);

  const {teamSessions} = teamSessionGroup?.teamSessionGroup ?? {};

  const playerSessions = useMemo(
    () => teamSessions?.flatMap(ts => ts.playerSessions ?? []),
    [teamSessions],
  );

  const playerSessionIds = useMemo(
    () => (playerSessions ?? []).filter(ps => ps.id).map(ps => ps.id!),
    [playerSessions],
  );

  const firstBaseline = useMemo(
    () =>
      playerSessions
        ?.filter(ps => ps.baseline && ps.startTime)
        ?.sort(
          (a, b) =>
            DateTime.fromISO(a.startTime as string).toUnixInteger() -
            DateTime.fromISO(b.startTime as string).toUnixInteger(),
        )
        ?.map(ps => ps.baseline)?.[0],
    [playerSessions],
  );

  const type = useMemo(
    () => teamSessions?.find(ts => ts.type)?.type || '',
    [teamSessions],
  );

  const playerTeamSessionTotals = useMemo(
    () => getPlayersTeamGroupSessionTotals(playerSessions || []),
    [playerSessions],
  );

  const segmentedSpeedAvgData = useMemo(
    () => playerSessions && playerSessions.map(ps => ps.speedAvgDataV2 ?? []),
    [playerSessions],
  );

  const segmentedAccelAvgData = useMemo(
    () => playerSessions && playerSessions.map(ps => ps.accelAvgDataV2 ?? []),
    [playerSessions],
  );

  const segmentedStrideRateAvgData = useMemo(
    () =>
      playerSessions && playerSessions.map(ps => ps.strideRateAvgDataV2 ?? []),
    [playerSessions],
  );

  const segmentedBalanceAvgData = useMemo(
    () => playerSessions && playerSessions.map(ps => ps.balanceAvgData ?? []),
    [playerSessions],
  );

  const segmentedHeartRateData = useMemo(
    () =>
      playerSessions &&
      playerSessions.map(ps =>
        (ps.heartRateData ?? []).map(hr => ({
          ...hr,
          value: hr.value1,
        })),
      ),
    [playerSessions],
  );

  const sessionStartAndEndTimes = useMemo(
    () =>
      playerSessions?.map(ps => ({
        startTime: ps.startTime || '',
        endTime: ps.endTime || '',
      })),
    [playerSessions],
  );

  const flattenedShifts = useMemo(
    () =>
      (playerSessions ?? []).flatMap(
        ps =>
          ps.playerSessionShifts?.filter(
            x => x.valid || (showShiftReview && x.canUpdateValid),
          ) || [],
      ),
    [playerSessions, showShiftReview],
  );

  const shiftClips = useMemo(
    () =>
      (playerSessions ?? []).flatMap(ps =>
        (ps.playerSessionShifts || [])?.filter(
          shift =>
            shift.valid && (shift.playerSessionVideoClips ?? []).length > 0,
        ),
      ) ?? [],
    [playerSessions],
  );

  const displayStartTime = useMemo(() => {
    if (playerTeamSessionTotals?.localDisplayStartTime) {
      return `${DateTime.fromJSDate(
        playerTeamSessionTotals.localDisplayStartTime,
      ).toFormat(
        'EEEE M/d/yyyy h:mm a',
      )} ${playerTeamSessionTotals?.timeZone ?? ''}`;
    }
    if (playerTeamSessionTotals?.startTime) {
      return DateTime.fromJSDate(playerTeamSessionTotals.startTime).toFormat(
        'EEEE M/d/yyyy h:mm a ZZZZ',
      );
    }
    return '';
  }, [playerTeamSessionTotals]);

  if (
    teamSessionGroup.status === 'loading' ||
    !teamSessionGroup?.teamSessionGroup
  ) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  if (!playerSessions || !playerSessions.length || !playerTeamSessionTotals) {
    return <></>;
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <PageTitle>{t('pages.playerSessionGroupDetail')}</PageTitle>
        <BackButton navigationUrl={backPath}>{backText}</BackButton>
        <PlayerSessionHeader
          number={playerSessions?.[0]?.player?.number}
          firstName={playerSessions?.[0]?.player?.profile?.firstName}
          lastName={playerSessions?.[0]?.player?.profile?.lastName}
          position={playerSessions?.[0]?.player?.profile?.position}
          profilePictureUrl={
            playerSessions?.[0]?.player?.profile?.profilePictureUrl
          }
        />
        <Box>
          <SubtitleContainer>
            <PageSubtitle sx={{flexGrow: 1, mr: 2}}>
              <StyledSessionType>
                {(type ?? '').toUpperCase()}
              </StyledSessionType>
              {displayStartTime}
            </PageSubtitle>
            <ExportSessionModal
              buttonSx={{mb: 2, alignSelf: 'center'}}
              playerSessionIds={playerSessionIds}
              playerSessionStartTime={playerSessions?.[0]?.startTime || ''}
            />
          </SubtitleContainer>
        </Box>
        {sessionType === 'game' && finalTeamSession && (
          <SessionMetaData
            disableEdit
            playerView={playerView}
            playerSession={finalTeamSession.playerSessions?.[0]}
          />
        )}
        {shiftClips.length > 0 && (
          <SessionVideoPlayer shiftClips={shiftClips} sessionType={type} />
        )}
        <Box>
          <ShiftTitleWrapper>
            <PageSubtitle>{t('shifts.title')}</PageSubtitle>
            <Box>
              <Button
                variant="outlined"
                onClick={() => setShowShiftReview(!showShiftReview)}>
                {t('shifts.shiftReview')}
              </Button>
            </Box>
          </ShiftTitleWrapper>
          <PlayerSessionShifts
            playerSessionIds={playerSessions?.map(ps => ps.id || 0) || []}
            type={type}
            shifts={flattenedShifts}
            inReview={showShiftReview}
            setInReview={setShowShiftReview}
            isProcessingVideo={
              !!playerSessions?.some(ps => ps.videoProcessingStartedAt)
            }
            baseline={firstBaseline}
          />
        </Box>
        <Box>
          <Paper sx={{mt: 5, mb: 5}}>
            <Grid container spacing={2}>
              <SessionGridItem
                label={t('sessionStats.strides')}
                value={
                  playerTeamSessionTotals.strideCountLeft +
                  playerTeamSessionTotals.strideCountRight
                }
              />
              <SessionGridItem
                label={
                  sessionType === SessionTypes.game
                    ? t('sessionStats.timeOnIce')
                    : t('sessionStats.activeTime')
                }
                value={
                  getTime(
                    sessionType === SessionTypes.game
                      ? playerTeamSessionTotals.timeOnIce
                      : playerTeamSessionTotals.activeTime,
                  ) ?? '--'
                }
              />
              <SessionGridItem
                label={t('sessionStats.strideTime')}
                value={getTime(playerTeamSessionTotals.strintTotalTime) ?? '--'}
              />
              <SessionGridItem
                label={t('sessionStats.agilityAvg')}
                value={
                  playerTeamSessionTotals.strintFilteredStrideRateAvgV2
                    ? playerTeamSessionTotals.strintFilteredStrideRateAvgV2.toFixed(
                        0,
                      )
                    : '--'
                }
                comparison={getComparisonFromBaseline({
                  stat: playerTeamSessionTotals.strintFilteredStrideRateAvgV2,
                  baseline: firstBaseline?.strintFilteredStrideRateAvgV2,
                })}
                unit={'spm'}
              />
              <SessionGridItem
                label={t('sessionStats.agilityMax')}
                value={
                  playerTeamSessionTotals.strintFilteredStrideRateMaxV2
                    ? playerTeamSessionTotals.strintFilteredStrideRateMaxV2.toFixed(
                        0,
                      )
                    : '--'
                }
                comparison={getComparisonFromBaseline({
                  stat: playerTeamSessionTotals.strintFilteredStrideRateMaxV2,
                  baseline: firstBaseline?.strintFilteredStrideRateMaxV2,
                })}
                unit={'spm'}
              />
              <SessionGridItem
                label={t('sessionStats.points')}
                value={playerTeamSessionTotals.points ?? '--'}
              />
              {playerTeamSessionTotals.hustleScoreTopLevel ? (
                <SessionGridItem
                  label={t('sessionStats.hustle')}
                  value={Math.round(
                    playerTeamSessionTotals.hustleScoreTopLevel,
                  )}
                  comparison={getComparisonFromBaseline({
                    stat: playerTeamSessionTotals.hustleScoreTopLevel,
                    baseline: firstBaseline?.hustleScoreTopLevel,
                  })}
                />
              ) : (
                <SessionGridItem
                  label={t('sessionStats.hustle')}
                  value={
                    playerTeamSessionTotals.hustleScore
                      ? getIntensityText(
                          Math.round(playerTeamSessionTotals.hustleScore),
                        ).toUpperCase()
                      : '--'
                  }
                />
              )}
              <SessionGridItem
                label={t('sessionStats.strideSpeedAvg')}
                value={
                  playerTeamSessionTotals.speedAvgV2
                    ? (
                        playerTeamSessionTotals.speedAvgV2 * speedMultiplier
                      ).toFixed(1)
                    : '--'
                }
                comparison={getComparisonFromBaseline({
                  stat: playerTeamSessionTotals.speedAvgV2,
                  baseline: firstBaseline?.speedAvgV2,
                })}
                unit={speedUnitLabel}
              />
              <SessionGridItem
                label={t('sessionStats.strideSpeedMax')}
                value={
                  playerTeamSessionTotals.speedMaxV2
                    ? (
                        playerTeamSessionTotals.speedMaxV2 * speedMultiplier
                      ).toFixed(1)
                    : '--'
                }
                comparison={getComparisonFromBaseline({
                  stat: playerTeamSessionTotals.speedMaxV2,
                  baseline: firstBaseline?.speedMaxV2,
                })}
                unit={speedUnitLabel}
              />
              <SessionGridItem
                label={t('sessionStats.explosivenessAvg')}
                value={
                  playerTeamSessionTotals.accelAvgV2
                    ? (playerTeamSessionTotals.accelAvgV2 / 9.8).toFixed(2)
                    : '--'
                }
                comparison={getComparisonFromBaseline({
                  stat: playerTeamSessionTotals.accelAvgV2,
                  baseline: firstBaseline?.accelAvgV2,
                })}
                unit={t('general.units.gAbbrev') as string}
              />
              <SessionGridItem
                label={t('sessionStats.explosivenessMax')}
                value={
                  playerTeamSessionTotals.accelMaxV2
                    ? (playerTeamSessionTotals.accelMaxV2 / 9.8).toFixed(2)
                    : '--'
                }
                comparison={getComparisonFromBaseline({
                  stat: playerTeamSessionTotals.accelMaxV2,
                  baseline: firstBaseline?.accelMaxV2,
                })}
                unit={t('general.units.gAbbrev') as string}
              />
              <SessionGridItem
                label={t('sessionStats.balancePercent')}
                value={
                  playerTeamSessionTotals.balance
                    ? `${Math.round(playerTeamSessionTotals.balance * 100)} / ${Math.round((1 - playerTeamSessionTotals.balance) * 100)}`
                    : '--'
                }
                comparison={getComparisonFromBaseline({
                  stat: playerTeamSessionTotals.balanceNormalized,
                  baseline: firstBaseline?.balanceNormalized,
                })}
              />
              {/* TODO, this item should show lock if user is not elite or pro */}
              <SessionGridItem
                label={t('sessionStats.energyRatio')}
                value={
                  getEnergyRatioFromFloat(
                    getEnergyRatio({
                      ratio: playerTeamSessionTotals?.energyRatio,
                    }),
                    true,
                  ) ?? '--'
                }
                unit={t('general.units.percentSymbol') as string}
                comparison={getComparisonFromBaseline({
                  stat: getEnergyRatio({
                    ratio: playerTeamSessionTotals?.energyRatio,
                  }),
                  baseline: firstBaseline?.energyRatio,
                })}
              />
              <SessionGridItem
                label={t('sessionStats.heartRateAvg')}
                value={playerTeamSessionTotals.hrAvg ?? '--'}
                unit={t('general.units.bpmAbbrev') as string}
                comparison={getComparisonFromBaseline({
                  stat: playerTeamSessionTotals.hrAvg,
                  baseline: firstBaseline?.hrAvg,
                })}
              />
            </Grid>
            {segmentedSpeedAvgData?.length &&
            segmentedSpeedAvgData.some(shrd => shrd.length) ? (
              <Box sx={{m: 5}}>
                <Typography variant="h4">
                  {t('statistics.strideSpeedDetail')}
                </Typography>
                <PlayerSessionGroupLineChart
                  data={segmentedSpeedAvgData}
                  metric={` ${speedUnitLabel}`}
                  sessionTimes={sessionStartAndEndTimes || []}
                />
              </Box>
            ) : null}
            {segmentedAccelAvgData?.length &&
            segmentedAccelAvgData.some(shrd => shrd.length) ? (
              <Box sx={{m: 5}}>
                <Typography variant="h4">
                  {t('statistics.explosivenessDetail')}
                </Typography>
                <PlayerSessionGroupLineChart
                  data={segmentedAccelAvgData}
                  metric={` ${t('general.units.gAbbrev') as string}`}
                  sessionTimes={sessionStartAndEndTimes || []}
                />
              </Box>
            ) : null}
            {segmentedStrideRateAvgData?.length &&
            segmentedStrideRateAvgData.some(shrd => shrd.length) ? (
              <Box sx={{m: 5}}>
                <Typography variant="h4">
                  {t('statistics.strideRateDetail')}
                </Typography>
                <PlayerSessionGroupLineChart
                  data={segmentedStrideRateAvgData}
                  metric={` ${t('general.units.spmAbbrev') as string}`}
                  sessionTimes={sessionStartAndEndTimes || []}
                />
              </Box>
            ) : null}
            {segmentedBalanceAvgData?.length &&
            segmentedBalanceAvgData.some(shrd => shrd.length) ? (
              <Box sx={{m: 5}}>
                <Typography variant="h4">
                  {t('statistics.balanceDetail')}
                </Typography>
                <Box>
                  <SizeMe>
                    {({size}) => (
                      <PlayerSessionGroupLineChart
                        data={segmentedBalanceAvgData}
                        sessionTimes={sessionStartAndEndTimes || []}
                        yAxisOverride={
                          <VictoryAxis
                            dependentAxis
                            tickValues={[0.05, 0.5, 0.95]}
                            tickFormat={tick => {
                              if (tick === 0.05) {
                                return 'Right';
                              } else if (tick === 0.5) {
                                return 'Balanced';
                              } else if (tick === 0.95) {
                                return 'Left';
                              }
                              return '';
                            }}
                            orientation="right"
                            style={yAxisStyle}
                            offsetX={size.width ?? 1120}
                          />
                        }
                        metric={''}
                      />
                    )}
                  </SizeMe>
                </Box>
              </Box>
            ) : null}
            {segmentedHeartRateData?.length &&
            segmentedHeartRateData.some(shrd => shrd.length) ? (
              <Box sx={{m: 5}}>
                <Typography variant="h4">
                  {t('statistics.hrAvgDetail')}
                </Typography>
                <PlayerSessionGroupLineChart
                  data={segmentedHeartRateData}
                  sessionTimes={sessionStartAndEndTimes || []}
                  metric={` ${t('general.units.bpmAbbrev') as string}`}
                />
              </Box>
            ) : null}
          </Paper>
        </Box>
        <PageSubtitle>{t('pages.playerSessions')}</PageSubtitle>
        <TableContainer sx={{mt: 3, mb: 5}} component={Paper}>
          <Table
            aria-label={
              t('ariaLabels.tables.playerSessionGroupSessions') as string
            }>
            <TableBody>
              {(playerSessions || []).map(ps => (
                <SessionListItem
                  key={`sessionListItem-${ps.id}`}
                  teamId={teamId || 0}
                  profileId={playerView && profile.profile.id}
                  familyId={
                    family.families.length
                      ? family.families.find(f => f.id).id
                      : 0
                  }
                  session={ps}
                  sessionTag={getPlayerSessionGroupTag(ps, teamSessions || [])}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </>
  );
};

export default PlayerSessionGroupDetail;
